import { Modal } from 'lib/components/Modal';
import { Form } from 'lib/components/Form';
import { GridInput } from 'lib/components/Card/Grid';
import { useState } from 'react';
import { ColumnButton } from 'lib/components/ColumnButton';
import { ColumnSelect } from 'lib/components/ColumnSelect';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import moment from 'moment';
import { getModelsFromQuery } from 'lib/model';
import { PublicationIssueModel } from 'lib/model/objects/publicationIssueModel';
import { AutoPaginationResponseBlock } from 'lib/types/api';
import { PublicationIssueStatus } from 'lib/types/publicationIssue';
import { ESnapshotExists, EOrganization } from 'lib/types';
import { getFirebaseContext } from 'utils/firebase';
import api from 'api';

export default function AutoPaginationTestModal({
  activeOrganization,
  onClose
}: {
  activeOrganization: ESnapshotExists<EOrganization>;
  onClose: () => void;
}) {
  const [publicationIssueModelID, setPublicationIssueModelID] = useState('');
  const [overrideExistingBlocks, setOverrideExistingBlocks] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [blockResults, setBlockResults] = useState<
    { block: AutoPaginationResponseBlock; url: string }[]
  >([]);
  const [blockErrors, setBlockError] = useState('');

  const { value: publicationIssueModel } = useAsyncEffect({
    fetchData: async () => {
      const relevantPubIssueDates = [];
      for (let i = -2; i < 8; i++) {
        relevantPubIssueDates.push(
          moment().add(i, 'days').format('YYYY-MM-DD')
        );
      }
      const relevantPubIssues = await getFirebaseContext()
        .publicationIssuesRef()
        .where('publisher', '==', activeOrganization.ref)
        .where('publicationDate', 'in', relevantPubIssueDates)
        .get();
      const pubIssueModels = getModelsFromQuery(
        PublicationIssueModel,
        getFirebaseContext(),
        relevantPubIssues
      );
      const relevantPubIssueModels = pubIssueModels.filter(
        model => model.modelData.status !== PublicationIssueStatus.DISABLED
      );
      const sortedPubIssueModels = relevantPubIssueModels.sort((a, b) =>
        Math.abs(
          moment().diff(moment(a.modelData.deadlineTimestamp?.toDate()), 'days')
        ) >
        Math.abs(
          moment().diff(moment(b.modelData.deadlineTimestamp?.toDate()), 'days')
        )
          ? 1
          : -1
      );
      setPublicationIssueModelID(sortedPubIssueModels[0].id);
      return sortedPubIssueModels;
    },
    dependencies: [activeOrganization?.id]
  });

  const runAutoPagination = async () => {
    setIsRunning(true);
    setBlockError('');
    setBlockResults([]);

    try {
      const { error, response: blocks } = await api.safePost(
        'pagination/test-auto-pagination',
        {
          publicationIssueModelID,
          overrideExistingBlocks
        }
      );
      if (error || !blocks) {
        setBlockError(error || 'Unknown error');
      } else {
        setBlockResults(blocks);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Modal
      id="auto-pagination-test-modal"
      onClose={onClose}
      title="Test Auto Pagination"
      size="2xl"
    >
      <div className="flex flex-col gap-4">
        <Form onSubmit={runAutoPagination}>
          <div className="grid grid-cols-2 gap-4 h-64 overflow-y-auto">
            <GridInput>
              <ColumnSelect
                id="publicationIssueModel"
                labelText="Publication Issue Model"
                value={publicationIssueModelID}
                onChange={setPublicationIssueModelID}
                options={
                  publicationIssueModel?.map(model => ({
                    label: `${model.modelData.publicationDate} - ${model.modelData.status} (${model.id})`,
                    value: model.id
                  })) || []
                }
                disabled={!publicationIssueModel}
              />
            </GridInput>
            <GridInput>
              <ColumnSelect
                id="overrideExistingBlocks"
                labelText="Override existing blocks"
                value={overrideExistingBlocks ? 'true' : 'false'}
                onChange={value => setOverrideExistingBlocks(value === 'true')}
                options={[
                  { value: 'true', label: 'Yes' },
                  { value: 'false', label: 'No' }
                ]}
              />
            </GridInput>
            <GridInput fullWidth>
              <ColumnButton
                buttonText="Run Auto Pagination"
                onClick={runAutoPagination}
                type="button"
                primary
                fullWidth
                disabled={isRunning || !publicationIssueModelID}
                loading={isRunning}
              />
            </GridInput>
          </div>
        </Form>
        {blockResults.length > 0 && (
          <>
            <div className="text-lg font-bold">Block Results</div>
            <div className="grid grid-cols-2 gap-4">
              {blockResults.map(result => (
                <div key={result.url} className="flex flex-col gap-1">
                  <div className="text-sm text-column-gray-500 text-center">
                    {result.block.whitespace} whitespace
                  </div>
                  <div className="text-sm text-column-gray-500 text-center">
                    {result.block.layout.width} x {result.block.layout.height}
                  </div>
                  <div
                    className="bg-column-gray-100 p-1 rounded-md cursor-pointer"
                    onClick={() => window.open(result.url, '_blank')}
                  >
                    <img
                      className="w-full h-80 object-contain cursor-pointer"
                      src={result.url}
                      alt="Auto pagination result"
                    />
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        <div>
          {blockErrors && <div className="text-red-500">{blockErrors}</div>}
        </div>
      </div>
    </Modal>
  );
}
